import React, { forwardRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Tab } from '@headlessui/react';
import cx from 'classnames';

import { FreeText } from 'components/ComponentV2';
import { Faqs } from 'components/opportunity-page';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { parseArrayResponse } from 'lib/utils/parser';
import { isDataAvailable } from 'utils/common.utils';

import styles from './DescriptionSummaryTabs.module.scss';

type DescriptionSummaryTabsProps = {
  tabListClassName?: string;
  tabPanelsClassName?: string;
  tabPanelSectionClassName?: string;
};

type TabPanelSectionProps = {
  className?: string;
  title: string;
  children: React.ReactNode;
};

const TabPanelSection: React.FC<TabPanelSectionProps> = ({ className, title, children }) => {
  return (
    <div className={cx(styles['tab-panel-section'], className)}>
      <h3 className={styles['tab-panel-section-title']}>{title}</h3>
      <div className={styles['tab-panel-section-content']}>{children}</div>
    </div>
  );
};

const SummarySections = ({
  className,
  requirements,
  skills,
  responsibilities,
}: {
  className?: string;
  requirements: string | string[];
  skills: string[];
  responsibilities: string | string[];
}) => {
  const [t] = useTranslation('opportunity');
  const { opportunity, aiFaqs } = useOpportunity();
  const { application_process } = opportunity;

  return (
    <>
      {isDataAvailable(requirements) && (
        <TabPanelSection title={t('summary-sections.requirements')} className={className}>
          <FreeText content={requirements} />
        </TabPanelSection>
      )}
      {isDataAvailable(skills) && (
        <TabPanelSection title={t('summary-sections.desired-skills')} className={className}>
          <FreeText content={skills} />
        </TabPanelSection>
      )}
      {isDataAvailable(responsibilities) && (
        <TabPanelSection title={t('summary-sections.responsibilities')} className={className}>
          <FreeText content={responsibilities} />
        </TabPanelSection>
      )}
      {isDataAvailable(application_process) && (
        <TabPanelSection title={t('summary-sections.application-process')} className={className}>
          <FreeText content={application_process} />
        </TabPanelSection>
      )}
      {aiFaqs.length > 0 && <Faqs list={aiFaqs} className={className} />}
    </>
  );
};

export const DescriptionSummaryTabs = forwardRef<HTMLDivElement, DescriptionSummaryTabsProps>(
  ({ tabListClassName, tabPanelsClassName, tabPanelSectionClassName }, ref) => {
    const [t] = useTranslation('opportunity');
    const { opportunity, aiFaqs } = useOpportunity();
    const {
      requirements: old_requirements,
      new_requirements,
      skills: skillsResponse,
      responsibilities: old_responsibilities,
      new_responsibilities,
      application_process,
    } = opportunity;
    const skills = parseArrayResponse(skillsResponse).map((skill) => skill.name);
    const requirements = new_requirements?.length > 0 ? new_requirements : old_requirements;
    const responsibilities = new_responsibilities?.length > 0 ? new_responsibilities : old_responsibilities;
    const hasAtleastOneSummarySection =
      isDataAvailable(requirements) ||
      isDataAvailable(skills) ||
      isDataAvailable(responsibilities) ||
      isDataAvailable(application_process) ||
      aiFaqs.length > 0;

    if (!hasAtleastOneSummarySection && !opportunity.description) {
      return null;
    }

    if (!opportunity.description) {
      return (
        <section className={styles['tab-panel']} ref={ref}>
          <SummarySections className={tabPanelSectionClassName} requirements={requirements} skills={skills} responsibilities={responsibilities} />
        </section>
      );
    } else if (!hasAtleastOneSummarySection) {
      return (
        <section className={styles['tab-panel']} ref={ref}>
          <div className={tabPanelSectionClassName}>
            <FreeText content={opportunity.description} />
          </div>
        </section>
      );
    }

    return (
      <div className={styles.container} ref={ref}>
        <Tab.Group>
          <Tab.List className={cx(styles['tab-list'], tabListClassName)}>
            <Tab className={styles.tab}>{t('summary.title')}</Tab>
            <Tab className={styles.tab}>{t('description.title')}</Tab>
          </Tab.List>
          <Tab.Panels className={cx(styles['tab-panels'], tabPanelsClassName)}>
            <Tab.Panel className={styles['tab-panel']}>
              <SummarySections className={tabPanelSectionClassName} requirements={requirements} skills={skills} responsibilities={responsibilities} />
            </Tab.Panel>
            <Tab.Panel className={styles['tab-panel']}>
              <div className={tabPanelSectionClassName}>
                <FreeText content={opportunity.description} />
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    );
  }
);

DescriptionSummaryTabs.displayName = 'DescriptionSummaryTabs';
